/* A set of pictures laid out on one sheet.
 *
 * The inverse of lib/tileSplit.js, and the same idea: a grid whose arithmetic
 * has to be exact. What you send instead of twenty attachments -- a proof
 * sheet, a moodboard, screenshots for a bug report, options for a client.
 *
 * IT IS BUILT FROM THE BATCH'S OWN OUTPUT, and nothing is uploaded for it.
 * The bytes are already here -- the batch fetched them from the CDN to build
 * the zip -- so this is the bargain downloadBatchPdf() strikes, and the sheet
 * never leaves the device.
 *
 * TWO LAYOUTS, AND THE FIRST VERSION HAD ONLY THE WORSE ONE.
 *
 * It shipped as square cells with every picture FITTED whole inside one,
 * which is right for a proof sheet and produces a page of postage stamps
 * floating in lavender the moment the set is a mix of portrait, panorama and
 * square -- which every real set is. Reported with the output beside a photo
 * collage: "I want something like this." The difference is not decoration. A
 * collage has no dead space, so each picture comes out two or three times
 * larger on the same sheet, which is the entire reason for putting them on
 * one.
 *
 *   COLLAGE  justified rows. Every row is exactly the full width, pictures
 *            keep their own proportions, nothing is cropped and nothing is
 *            padded. Row heights differ, and that is what removes the gaps.
 *
 *   GRID     uniform square cells, filled or fitted. FILL crops each picture
 *            to a square; FIT letterboxes it. Fill is the tidy one and loses
 *            the edges; fit shows the whole frame and spends the corners.
 *            Both were asked for by name.
 *
 *     sheetColumns()  how many across, when nobody said
 *     justifyRows()   the collage. The risk lives here
 *     sheetPlan()     every rectangle on the page. Pure; no canvas
 *     drawSheet()     the painter
 */

/* THE SQUARE ROOT, which is what a contact sheet has always done.
 *
 * Twelve pictures want 4x3 rather than 12x1: the sheet is looked at on a
 * screen or printed on a page, and both are roughly square, so the grid
 * closest to square wastes least.
 *
 * Capped at 6 because past that the thumbnails stop being identifiable, which
 * is the one job a grid sheet has. The collage has no such cap -- it decides
 * its own rows from the shapes it is given.
 */
export const MAX_SHEET_COLUMNS = 6

export function sheetColumns(count, asked = 0) {
    if (asked >= 1) {
        return Math.min(asked, Math.max(1, count))
    }
    return Math.max(1, Math.min(MAX_SHEET_COLUMNS, Math.ceil(Math.sqrt(count))))
}

/* One picture inside one box, CONTAINED. Never enlarged: a 200px thumbnail
 * blown up to a 400px cell is a blurred rectangle pretending to be a
 * photograph, the rule fitWithin() in lib/videoFrames.js also makes. */
export function fitWithin(w, h, boxW, boxH) {
    if (!(w > 0 && h > 0 && boxW > 0 && boxH > 0)) {
        return {w: 0, h: 0}
    }
    const scale = Math.min(boxW / w, boxH / h, 1)
    return {w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale))}
}

/* The part of a picture that FILLS a box without distorting it.
 *
 * The other half of "turn each image into a square": the largest
 * sub-rectangle whose shape matches the box.
 *
 * FOCUS IS WHERE IN THE PICTURE THAT WINDOW SITS, 0 to 1 on each axis, and
 * 0.5 is the middle. It defaults to the middle because guessing where the
 * subject is would be wrong often enough to be worse than predictable -- but
 * a centre crop takes the head off a portrait about as often as it works,
 * which is what dragging the picture in the preview is for. Only the axis
 * that is actually being cropped moves; the other has nothing to slide along
 * and its focus is ignored rather than quietly distorting anything.
 */
export function coverRect(w, h, boxW, boxH, focus = null) {
    if (!(w > 0 && h > 0 && boxW > 0 && boxH > 0)) {
        return {x: 0, y: 0, w: w || 1, h: h || 1}
    }
    const clamp01 = (v) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5)
    const fx = clamp01(focus && focus.x)
    const fy = clamp01(focus && focus.y)
    const want = boxW / boxH
    const have = w / h
    if (have > want) {
        const sw = Math.max(1, Math.round(h * want))
        return {x: Math.round((w - sw) * fx), y: 0, w: sw, h}
    }
    const sh = Math.max(1, Math.round(w / want))
    return {x: 0, y: Math.round((h - sh) * fy), w, h: sh}
}

/* Which way a picture can be slid inside a square, and by how much.
 *
 * A landscape photograph in a square cell is cropped left and right, so it
 * slides sideways and not at all up and down; a portrait is the reverse; a
 * square is not cropped and does not slide. The preview asks this before
 * offering the gesture, so a picture that cannot move never shows a grab
 * cursor -- a control that does nothing is worse than no control.
 */
export function panRange(w, h, boxW, boxH) {
    if (!(w > 0 && h > 0 && boxW > 0 && boxH > 0)) {
        return {x: 0, y: 0}
    }
    const r = coverRect(w, h, boxW, boxH)
    return {x: Math.max(0, w - r.w), y: Math.max(0, h - r.h)}
}

/* THE COLLAGE, and the whole of why it has no gaps.
 *
 * Pictures are taken in order and added to a row until the row is wide
 * enough; the row is then scaled so it fills the width EXACTLY. Each row
 * therefore has its own height, and that is the trade: uniform rows force
 * either cropping or padding, and a row free to be its own height needs
 * neither.
 *
 * HOW MANY ROWS IS DERIVED, NOT GUESSED. A row whose pictures' aspect ratios
 * sum to S is W/S tall across width W, so R such rows come to about W*R*R/S
 * altogether. Setting that equal to W -- a sheet about as tall as it is wide,
 * which is what a screen and a sheet of paper both are -- gives R = sqrt(S).
 * Six landscapes sum to about 9 and get 3 rows; six portraits sum to about 4
 * and get 2.
 *
 * THE ROWS ARE BALANCED, NOT FILLED GREEDILY, and that is the second version
 * of this function. Taking pictures until a row is full and starting a new
 * one is the obvious way and it leaves a STUB: eight pictures came out 3, 4
 * and then ONE ALONE, flush left, with three quarters of the last row white.
 * Which is the dead space this whole layout exists to remove, moved to the
 * bottom of the sheet. Seen by rendering it and looking at it.
 *
 * Capping the stub's height -- the first attempt -- only chooses which way it
 * looks wrong: justify it and one frame is three times the height of
 * everything above; do not, and the row is mostly empty. The fix is to have
 * no stub. The pictures are partitioned into exactly R runs whose aspect sums
 * are as even as possible, and every run then justifies to the full width.
 */
function balance(list, rows) {
    const n = list.length
    if (rows <= 1) {
        return [list.map((_, i) => i)]
    }
    if (rows >= n) {
        return list.map((_, i) => [i])
    }
    // Prefix sums, so a run's total is one subtraction rather than a loop.
    const upto = [0]
    for (let i = 0; i < n; i++) {
        upto.push(upto[i] + list[i])
    }

    /* SQUARED DEVIATION FROM THE FAIR SHARE, and not "minimise the largest
     * run", which was the first objective and looked balanced on paper.
     *
     * A row's HEIGHT is the width divided by its aspect sum, so equal sums
     * mean equal heights. Minimising only the largest sum leaves the others
     * free -- there are many arrangements with the same maximum and the DP
     * takes whichever it meets first. Measured on eight mixed pictures it
     * chose rows of 2, 2 and 4 whose heights came out 549, 353 and 267: every
     * row filled the width, and the sheet still looked wrong because the top
     * row's pictures were twice the size of the bottom row's.
     *
     * Squaring the distance from total/R penalises BOTH ends, so a run that
     * is too small costs as much as one that is too large. */
    const share = upto[n] / rows
    const cost = []
    const cut = []
    for (let i = 0; i <= n; i++) {
        cost.push(new Array(rows + 1).fill(Infinity))
        cut.push(new Array(rows + 1).fill(0))
    }
    cost[0][0] = 0
    for (let r = 1; r <= rows; r++) {
        for (let i = r; i <= n; i++) {
            for (let j = r - 1; j < i; j++) {
                const off = (upto[i] - upto[j]) - share
                const total = cost[j][r - 1] + off * off
                if (total < cost[i][r]) {
                    cost[i][r] = total
                    cut[i][r] = j
                }
            }
        }
    }

    const out = []
    let end = n
    for (let r = rows; r >= 1; r--) {
        const start = cut[end][r]
        const run = []
        for (let k = start; k < end; k++) {
            run.push(k)
        }
        out.unshift(run)
        end = start
    }
    return out.filter((run) => run.length)
}

export function justifyRows(aspects, width, gap = 0, rows = 0) {
    const list = (aspects || []).map((a) => (a > 0 ? a : 1))
    if (!list.length || !(width > 0)) {
        return []
    }
    const total = list.reduce((a, b) => a + b, 0)
    const wanted = rows >= 1
        ? Math.min(rows, list.length)
        : Math.max(1, Math.min(list.length, Math.round(Math.sqrt(total))))

    return balance(list, wanted).map((items) => {
        const sum = items.reduce((a, i) => a + list[i], 0)
        /* EVERY row justifies, the last one included, and that is what having
         * no stub buys. The greedy version had to special-case a final row
         * holding one picture, because justifying it made a single frame three
         * times the height of everything above it -- and refusing to justify
         * it left three quarters of that row white. With the runs balanced,
         * neither case can arise. */
        return {items, height: (width - gap * (items.length - 1)) / sum}
    })
}

/* HOW FAR A POINTER HAS TO TRAVEL TO SLIDE A CROP FROM END TO END.
 *
 * Returned in focus units per drawn pixel, so the caller multiplies by the
 * distance dragged and subtracts.
 *
 * THE OBVIOUS ANSWER IS 1:1 -- move the pointer a pixel, move the picture
 * under it a pixel -- and on its own it does not work. The travel 1:1 needs
 * is (w - h) * cell / h for a landscape in a square, which on a 1600x1067
 * photograph in a 400px cell is 200px, half the cell. On an 1800x760
 * panorama it is 547px, and on a 2400x600 it is 1200px: three cell widths,
 * all of them outside the square being dragged in. That matters because
 * leaving the square is what turns a recentre into a reorder, so under a
 * strict 1:1 rule the widest pictures -- the ones most in need of
 * recentring -- could not be recentred at all.
 *
 * So the step is 1:1 OR whatever covers the whole range in one cell width,
 * whichever is faster. Ordinary pictures track the pointer exactly; only the
 * extreme ones move quicker than the finger, and they are the ones where the
 * alternative is not moving at all.
 */
export function panStep(srcLen, drawLen, panLen) {
    if (!(panLen > 0) || !(drawLen > 0) || !(srcLen > 0)) {
        return 0
    }
    return Math.max(srcLen, panLen) / (drawLen * panLen)
}

/* Shorten a filename to what fits, from the MIDDLE and one character at a
 * time.
 *
 * Truncating the end leaves every caption reading `IMG_2026092...`, and the
 * identifying half of a camera filename is at the end. Counting down from the
 * full length rather than starting at half: a name one pixel too wide should
 * lose one character, not half of itself. Both halves of that were found by
 * rendering a real sheet and looking at it.
 */
export function fitLabel(ctx, text, maxWidth) {
    const s = String(text || "")
    if (!s || ctx.measureText(s).width <= maxWidth) {
        return s
    }
    for (let keep = s.length - 1; keep > 2; keep--) {
        const head = s.slice(0, Math.ceil(keep / 2))
        const tail = s.slice(s.length - Math.floor(keep / 2))
        const out = `${head}…${tail}`
        if (ctx.measureText(out).width <= maxWidth) {
            return out
        }
    }
    return "…"
}

export const SHEET_MODES = ["collage", "fill", "fit"]
export const SHEET_GAPS = {none: 0, thin: 10, wide: 26}

/* WHAT THE SHEET IS PAINTED ON.
 *
 * drawSheet() has always been able to paint a dark sheet -- there was a `dark`
 * boolean -- and nothing in the application ever passed it, so every sheet
 * came out white. Asked for with a photograph of a conference collage on
 * black, which is a thing a white sheet cannot be.
 *
 * The same five colours SVG_BACKGROUNDS offers, minus transparent: a JPEG has
 * no alpha, so a transparent swatch here would be a control that lies. Reusing
 * that list rather than inventing another means the two pickers on this site
 * agree, and every name already exists in `colours.*` in all ten catalogues.
 *
 * Hex here and not in the component, for the reason SVG_BACKGROUNDS and
 * lib/starters.js both record: tests/test_palette.py refuses a raw hex literal
 * in a .vue file.
 */
export const SHEET_GROUNDS = [
    {key: "white", hex: "#ffffff"},
    {key: "black", hex: "#000000"},
    {key: "navy", hex: "#1b2a4a"},
    {key: "sand", hex: "#c8a06a"},
    {key: "lightGreen", hex: "#90ee90"},
]

/* Empty means white, so $reset() gives the sheet somebody sends by default --
 * the same idiom DEFAULT_SVG_BACKGROUND uses for transparent. */
export const DEFAULT_SHEET_GROUND = "#ffffff"

/* THE LABELS' COLOUR, DECIDED BY THE GROUND RATHER THAN STORED BESIDE IT.
 *
 * Exactly memeOutline()'s rule, including the number: a Rec. 709 luma of 140.
 * One threshold on this site rather than two, and it means a ground added to
 * the list later cannot arrive with the wrong ink by omission -- there is
 * nothing to forget to set.
 *
 * tests/grid_test.mjs refuses any ground within 15 of the threshold, which is
 * the guard meme_layout_test.mjs already applies to the caption colours: a hex
 * nudged across the line flips every filename from dark to light with nothing
 * to say so, which on a sheet reads as a rendering fault rather than a
 * setting.
 */
export function sheetInk(hex) {
    return sheetLuma(hex) > 140 ? "#3a3a44" : "#e8e6f0"
}

export function sheetLuma(hex) {
    const n = parseInt(String(hex || "").slice(1), 16)
    if (!Number.isFinite(n)) {
        return 255
    }
    return 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)
}

/* The cell's own ground, a step off the sheet's, so a fitted picture with
 * white edges still reads as a picture.
 *
 * A step TOWARDS THE INK, which is one rule that works on every ground: on
 * white it darkens, on black it lightens, and either way the recess is in the
 * direction the eye is already reading. It used to be a second hard-coded pair
 * beside the first, which is one more thing to forget when a colour is added.
 */
export function sheetWell(hex) {
    const ground = parseInt(String(hex || "").slice(1), 16)
    const ink = parseInt(sheetInk(hex).slice(1), 16)
    if (!Number.isFinite(ground)) {
        return "#f1eff7"
    }
    const mix = (shift) => {
        const g = (ground >> shift) & 255
        const i = (ink >> shift) & 255
        return Math.round(g + (i - g) * 0.08)
    }
    const hex2 = (v) => v.toString(16).padStart(2, "0")
    return "#" + hex2(mix(16)) + hex2(mix(8)) + hex2(mix(0))
}

/* EVERY RECTANGLE ON THE PAGE, computed without a canvas.
 *
 * Separated for the reason asciiPngLayout() is: the geometry is where the
 * mistakes are and a browser is not needed to find them. Each cell carries
 * three rectangles and they are not the same thing:
 *
 *   cell  the slot. What gets the well behind it, and what a pointer is
 *         tested against when the pictures are dragged into a new order
 *   draw  where the picture actually lands inside that slot
 *   src   which part of the picture is used -- all of it, except in fill
 *         mode where it is the centred crop
 *
 * The caps are lib/imageTransform.js's MAX_EDGE and MAX_AREA, passed in
 * rather than imported, and they bind in ordinary use: twenty pictures at six
 * across in 400px cells is already over 2,500px wide. iOS Safari abandons a
 * canvas above about 16.7 MP and returns a BLANK one rather than an error.
 */
export function sheetPlan({items, mode = "collage", columns = 0,
                           gap = SHEET_GAPS.thin, pad = null, labels = true,
                           cell = 400, width = 1200,
                           maxEdge = 4096, maxArea = 12500000} = {}) {
    const list = (items || []).filter((it) => it && it.w > 0 && it.h > 0)
    if (!list.length) {
        return null
    }
    const how = SHEET_MODES.indexOf(mode) >= 0 ? mode : "collage"

    /* THE MARGIN IS THE SPACING, unless a caller says otherwise.
     *
     * They were separate, and the margin was a constant 28 -- so "Spacing:
     * None" removed the gaps BETWEEN the pictures and left a white border
     * around the whole sheet, which is three quarters of the way to edge-to-
     * edge and looks like a bug rather than a setting. One control should
     * mean one thing everywhere it applies. */
    const margin = pad === null ? gap : pad

    const build = (scale) => {
        const g = Math.max(0, Math.round(gap * scale))
        const p = Math.max(0, Math.round(margin * scale))
        const cells = []
        let W
        let H

        if (how === "collage") {
            /* A collage justifies to whatever width it is given, so unlike a
             * grid something has to DECIDE that width rather than derive it.
             * It is the one number this layout takes from outside. */
            const inner = Math.max(80, Math.round(width * scale))
            const labelH = labels ? Math.max(12, Math.round(inner * 0.022)) : 0
            const rows = justifyRows(list.map((it) => it.w / it.h), inner, g)
            let y = p
            for (const row of rows) {
                let x = p
                const h = Math.max(1, Math.round(row.height))
                for (const i of row.items) {
                    const it = list[i]
                    const w = Math.max(1, Math.round(h * (it.w / it.h)))
                    cells.push({
                        index: i,
                        cell: {x, y, w, h: h + labelH},
                        draw: {x, y, w, h},
                        src: {x: 0, y: 0, w: it.w, h: it.h},
                        label: labelH ? {y: y + h, h: labelH} : null
                    })
                    x += w + g
                }
                y += h + labelH + g
            }
            W = inner + p * 2
            H = (rows.length ? y - g : p) + p
        } else {
            const c = Math.max(1, Math.round(cell * scale))
            const cols = sheetColumns(list.length, columns)
            const rows = Math.ceil(list.length / cols)
            const labelH = labels ? Math.max(12, Math.round(c * 0.085)) : 0
            list.forEach((it, i) => {
                const col = i % cols
                const row = Math.floor(i / cols)
                const x = p + col * (c + g)
                const y = p + row * (c + labelH + g)
                const common = {
                    index: i,
                    cell: {x, y, w: c, h: c + labelH},
                    label: labelH ? {y: y + c, h: labelH} : null
                }
                if (how === "fill") {
                    cells.push(Object.assign(common, {
                        draw: {x, y, w: c, h: c},
                        src: coverRect(it.w, it.h, c, c, it.focus),
                        // What the preview needs to know before it offers a
                        // drag: a square picture in a square cell cannot move.
                        pan: panRange(it.w, it.h, c, c)
                    }))
                } else {
                    const f = fitWithin(it.w, it.h, c, c)
                    cells.push(Object.assign(common, {
                        draw: {
                            x: x + Math.round((c - f.w) / 2),
                            y: y + Math.round((c - f.h) / 2),
                            w: f.w, h: f.h
                        },
                        src: {x: 0, y: 0, w: it.w, h: it.h}
                    }))
                }
            })
            W = p * 2 + cols * c + (cols - 1) * g
            H = p * 2 + rows * (c + labelH) + (rows - 1) * g
        }
        return {width: Math.round(W), height: Math.round(H), cells,
                gap: g, pad: p, mode: how, labels: !!labels}
    }

    let scale = 1
    for (;;) {
        const plan = build(scale)
        if (plan.width <= maxEdge && plan.height <= maxEdge
            && plan.width * plan.height <= maxArea) {
            return plan
        }
        if (scale <= 0.16) {
            // At the floor and still too big is not a small sheet, it is no
            // sheet -- and saying so beats the blank canvas iOS hands back.
            return null
        }
        scale -= 0.08
    }
}

/* Which cell a point is in, for dragging one picture onto another.
 *
 * Reading order is not enough: a collage's rows have different heights and
 * the last one can be short, so "which column is this x in" has no answer.
 * The cells are tested directly, which also lets one function serve both
 * layouts.
 */
export function cellAtPoint(plan, x, y) {
    if (!plan) {
        return -1
    }
    for (const c of plan.cells) {
        if (x >= c.cell.x && x < c.cell.x + c.cell.w
            && y >= c.cell.y && y < c.cell.y + c.cell.h) {
            return c.index
        }
    }
    return -1
}

/* Move one picture to another position, keeping the rest in order.
 *
 * MOVE, not swap. Swapping is a line shorter and wrong: dragging the fifth
 * picture to the front should push the others along, which is what anybody
 * rearranging a set means. Swapping sends whatever was first out to position
 * five, which nobody asked for and everybody then has to undo.
 */
export function moveItem(order, from, to) {
    const out = (order || []).slice()
    if (from < 0 || to < 0 || from >= out.length || to >= out.length || from === to) {
        return out
    }
    const [it] = out.splice(from, 1)
    out.splice(to, 0, it)
    return out
}

const SHEET_SANS =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

/* Paint it. `images` is [{img, name, w, h}] in the order they should appear.
 *
 * The canvas is created here rather than taken as an argument, so nothing can
 * hand this a context whose font or alignment was set elsewhere -- the bug
 * captionMeasurer() on the meme page exists to prevent.
 */
export function drawSheet({images, mode = "collage", columns = 0,
                           gap = SHEET_GAPS.thin, labels = true, cell = 400,
                           width = 1200, ground = DEFAULT_SHEET_GROUND,
                           doc = document,
                           maxEdge = 4096, maxArea = 12500000} = {}) {
    const list = (images || []).filter((e) => e && e.img)
    if (!list.length) {
        return null
    }
    const items = list.map((e) => ({
        w: e.w || e.img.naturalWidth || e.img.width || 1,
        h: e.h || e.img.naturalHeight || e.img.height || 1,
        focus: e.focus || null
    }))
    const plan = sheetPlan({items, mode, columns, gap, labels, cell, width,
                            maxEdge, maxArea})
    if (!plan) {
        return null
    }

    const canvas = doc.createElement("canvas")
    canvas.width = plan.width
    canvas.height = plan.height
    const ctx = canvas.getContext("2d")

    const paper = ground || DEFAULT_SHEET_GROUND
    const ink = sheetInk(paper)
    /* Only in fit mode: a filled cell and a collage leave none of it showing,
     * and painting it anyway would be a rectangle nobody ever sees. */
    const well = sheetWell(paper)

    ctx.fillStyle = paper
    ctx.fillRect(0, 0, plan.width, plan.height)
    ctx.textBaseline = "top"
    ctx.textAlign = "center"

    for (const c of plan.cells) {
        const e = list[c.index]
        if (plan.mode === "fit") {
            ctx.fillStyle = well
            ctx.fillRect(c.cell.x, c.cell.y, c.cell.w, c.cell.w)
        }
        if (c.draw.w > 0 && c.draw.h > 0) {
            ctx.drawImage(e.img, c.src.x, c.src.y, c.src.w, c.src.h,
                          c.draw.x, c.draw.y, c.draw.w, c.draw.h)
        }
        if (c.label) {
            const px = Math.max(9, Math.round(c.label.h * 0.62))
            ctx.font = `${px}px ${SHEET_SANS}`
            ctx.fillStyle = ink
            ctx.fillText(fitLabel(ctx, e.name, c.cell.w),
                         c.cell.x + c.cell.w / 2,
                         c.label.y + Math.round((c.label.h - px) / 2))
        }
    }

    return canvas
}

/* What the sheet is called on disk.
 *
 * "contact-sheet.jpg" in a downloads folder is a file nobody can identify a
 * week later -- the argument outputName() makes in lib/asciiText.js and
 * batchOutputName() makes for the versions table. The first picture's stem
 * names it, with the batch's own trailing width stripped, because
 * `beach_1280_sheet.jpg` reads as a width that is not the sheet's.
 *
 * NOT assembledName() from lib/pdfBuild.js, which is the same idea and hard-
 * codes ".pdf". Calling it and rewriting the extension would depend on a
 * detail of that function that nothing promises.
 */
export function sheetName(firstName, count, ext = "jpg", fallback = "pictures") {
    const noExt = String(firstName || "").replace(/\.[^.]+$/, "")
    const stem = noExt.replace(/[_-]\d{1,5}$/, "").trim()
    return `${stem || fallback}-sheet-of-${count}.${ext}`
}
